import { apiService } from './api'

// Types for combined library data
export interface CombinedExercise {
  id: string;
  name: string;
  description?: string;
  type: 'compound' | 'isolation' | 'cardio';
  repRange: [number, number];
  sets?: number;
  restTime?: number;
  difficulty: number;
  rating: number;
  danger: number;
  symmetryMode: 'symmetrical' | 'unilateral' | 'bilateral';
  muscleActivations: { [muscleGroupId: string]: number };
  equipment: { [equipmentId: string]: number };
  targetAdaptations: { [adaptationId: string]: number };
  instructions: string[];
  techniqueChecklist: string[];
  videoUrl?: string;
  imageUrl?: string;
  tags?: string[];
  isActive: boolean;
  
  // Metadata to distinguish source
  source: 'global' | 'coach';
  coachId?: string;
  coachName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CombinedEquipment {
  id: string;
  name: string;
  description?: string;
  category: 'Cardio' | 'Strength' | 'Functional' | 'Accessories' | 'Technology';
  brand?: string;
  model?: string;
  specifications?: {
    weight?: number;
    dimensions?: string;
    powerRequirement?: string;
    capacity?: string;
  };
  
  // Metadata to distinguish source
  source: 'global' | 'coach';
  coachId?: string;
  coachName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CombinedMuscleGroup {
  id: string;
  name: string;
  description?: string;
  anatomicalRegion?: string;
  split: boolean;
  sourceIdLeft?: string;
  sourceIdRight?: string;
  
  // Always global for muscle groups
  source: 'global';
  createdAt: string;
  updatedAt: string;
}

export interface CombinedAdaptation {
  id: string;
  name: string;
  description?: string;
  
  // Always global for adaptations
  source: 'global';
  createdAt: string;
  updatedAt: string;
}

export interface LibraryFilters {
  search?: string;
  muscleGroup?: string;
  equipment?: string;
  category?: string;
  type?: string;
  difficulty?: string;
  source?: 'global' | 'coach' | 'all';
  coachId?: string;
  includeInactive?: boolean;
}

export interface LibraryResponse<T> {
  data: T[];
  total: number;
  globalCount: number;
  coachCount: number;
  pagination?: {
    page: number;
    limit: number;
    pages: number;
  };
}

export interface LibraryStats {
  exercises: {
    total: number;
    global: number;
    coach: number;
  };
  equipment: {
    total: number;
    global: number;
    coach: number;
  };
  muscleGroups: {
    total: number;
    global: number;
  };
  adaptations: {
    total: number;
    global: number;
  };
}

export class LibraryService {
  /**
   * Get combined exercises from both global library and coach-created exercises
   */
  static async getExercises(
    filters: LibraryFilters = {},
    page: number = 1,
    limit: number = 20
  ): Promise<LibraryResponse<CombinedExercise>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== undefined && v !== '')
        )
      });

      const response = await apiService.request('GET', `/api/library/exercises?${params}`);
      
      if (response.success && response.data) {
        const responseData = response.data as any;
        return {
          data: responseData.data || responseData || [],
          total: responseData.total || 0,
          globalCount: responseData.globalCount || 0,
          coachCount: responseData.coachCount || 0,
          pagination: responseData.pagination || { page, limit: 50, total: 0, pages: 1 }
        };
      } else {
        throw new Error(response.error || 'Failed to fetch exercises');
      }
    } catch (error) {
      console.error('Error fetching library exercises:', error);
      throw error;
    }
  }

  /**
   * Get combined equipment from both global library and coach-created equipment
   */
  static async getEquipment(
    filters: LibraryFilters = {},
    page: number = 1,
    limit: number = 20
  ): Promise<LibraryResponse<CombinedEquipment>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== undefined && v !== '')
        )
      });

      const response = await apiService.request('GET', `/api/library/equipment?${params}`);
      
      if (response.success && response.data) {
        const responseData = response.data as any;
        return {
          data: responseData.data || responseData || [],
          total: responseData.total || 0,
          globalCount: responseData.globalCount || 0,
          coachCount: responseData.coachCount || 0,
          pagination: responseData.pagination || { page, limit: 50, total: 0, pages: 1 }
        };
      } else {
        throw new Error(response.error || 'Failed to fetch equipment');
      }
    } catch (error) {
      console.error('Error fetching library equipment:', error);
      throw error;
    }
  }

  /**
   * Get muscle groups (only from global library)
   */
  static async getMuscleGroups(filters: LibraryFilters = {}): Promise<LibraryResponse<CombinedMuscleGroup>> {
    try {
      const params = new URLSearchParams(
        Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== undefined && v !== '')
        )
      );

      const response = await apiService.request('GET', `/api/library/muscle-groups?${params}`);
      
      if (response.success && response.data) {
        const responseData = response.data as any;
        return {
          data: responseData.data || responseData || [],
          total: responseData.total || 0,
          globalCount: responseData.globalCount || 0,
          coachCount: responseData.coachCount || 0,
        };
      } else {
        throw new Error(response.error || 'Failed to fetch muscle groups');
      }
    } catch (error) {
      console.error('Error fetching library muscle groups:', error);
      throw error;
    }
  }

  /**
   * Get adaptations (only from global library)
   */
  static async getAdaptations(filters: LibraryFilters = {}): Promise<LibraryResponse<CombinedAdaptation>> {
    try {
      const params = new URLSearchParams(
        Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== undefined && v !== '')
        )
      );

      const response = await apiService.request('GET', `/api/library/adaptations?${params}`);
      
      if (response.success && response.data) {
        const responseData = response.data as any;
        return {
          data: responseData.data || responseData || [],
          total: responseData.total || 0,
          globalCount: responseData.globalCount || 0,
          coachCount: responseData.coachCount || 0,
        };
      } else {
        throw new Error(response.error || 'Failed to fetch adaptations');
      }
    } catch (error) {
      console.error('Error fetching library adaptations:', error);
      throw error;
    }
  }

  /**
   * Search across all library content
   */
  static async searchLibrary(
    query: string,
    filters: LibraryFilters = {},
    page: number = 1,
    limit: number = 20
  ): Promise<{
    exercises: LibraryResponse<CombinedExercise>;
    equipment: LibraryResponse<CombinedEquipment>;
    muscleGroups: LibraryResponse<CombinedMuscleGroup>;
    adaptations: LibraryResponse<CombinedAdaptation>;
  }> {
    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        limit: limit.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== undefined && v !== '')
        )
      });

      const response = await apiService.request('GET', `/api/library/search?${params}`);
      
      if (response.success && response.data) {
        const responseData = response.data as any;
        return {
          exercises: responseData.exercises || { data: [], total: 0, globalCount: 0, coachCount: 0 },
          equipment: responseData.equipment || { data: [], total: 0, globalCount: 0, coachCount: 0 },
          muscleGroups: responseData.muscleGroups || { data: [], total: 0, globalCount: 0, coachCount: 0 },
          adaptations: responseData.adaptations || { data: [], total: 0, globalCount: 0, coachCount: 0 },
        };
      } else {
        throw new Error(response.error || 'Failed to search library');
      }
    } catch (error) {
      console.error('Error searching library:', error);
      throw error;
    }
  }

  /**
   * Get library statistics
   */
  static async getStats(): Promise<LibraryStats> {
    try {
      const response = await apiService.request('GET', '/api/library/stats');
      
      if (response.success && response.data) {
        return response.data as LibraryStats;
      } else {
        throw new Error(response.error || 'Failed to fetch library statistics');
      }
    } catch (error) {
      console.error('Error fetching library stats:', error);
      throw error;
    }
  }

  /**
   * Get exercises filtered by muscle group (for program generation)
   */
  static async getExercisesByMuscleGroup(
    muscleGroupId: string,
    filters: Omit<LibraryFilters, 'muscleGroup'> = {}
  ): Promise<CombinedExercise[]> {
    try {
      const result = await this.getExercises({ ...filters, muscleGroup: muscleGroupId }, 1, 100);
      return result.data;
    } catch (error) {
      console.error('Error fetching exercises by muscle group:', error);
      throw error;
    }
  }

  /**
   * Get exercises filtered by equipment (for program generation)
   */
  static async getExercisesByEquipment(
    equipmentId: string,
    filters: Omit<LibraryFilters, 'equipment'> = {}
  ): Promise<CombinedExercise[]> {
    try {
      const result = await this.getExercises({ ...filters, equipment: equipmentId }, 1, 100);
      return result.data;
    } catch (error) {
      console.error('Error fetching exercises by equipment:', error);
      throw error;
    }
  }

  /**
   * Get all global exercises (for program generation baseline)
   */
  static async getGlobalExercises(limit: number = 1000): Promise<CombinedExercise[]> {
    try {
      const result = await this.getExercises({ source: 'global' }, 1, limit);
      return result.data;
    } catch (error) {
      console.error('Error fetching global exercises:', error);
      throw error;
    }
  }

  /**
   * Get coach-specific exercises (for program generation customization)
   */
  static async getCoachExercises(coachId?: string, limit: number = 1000): Promise<CombinedExercise[]> {
    try {
      const result = await this.getExercises({ 
        source: 'coach', 
        coachId 
      }, 1, limit);
      return result.data;
    } catch (error) {
      console.error('Error fetching coach exercises:', error);
      throw error;
    }
  }
}

export default LibraryService;

